import { useSuspenseQuery } from '@apollo/client';
import { Suspense, useEffect } from 'react';

import { ErrorBoundary } from '@xing-com/crate-core-error-boundary';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';
import {
  EntityPageModuleDocument,
  ModuleError,
  NewsContextProvider,
  NewsList,
  NewsListSkeleton,
} from '@xing-com/crate-entity-pages-news';
import { Column, Row } from '@xing-com/xing-grid';

import * as Styled from './news-subpage.style';

const NewsSubpageInner: React.FC = () => {
  const { pageContext } = usePageContext();
  const { pageId } = pageContext ?? {};
  const { data, error, refetch } = useSuspenseQuery(EntityPageModuleDocument, {
    variables: { id: pageId ?? '', moduleType: 'news' },
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (error) {
    return <ModuleError refetch={refetch} />;
  }

  return (
    <NewsContextProvider
      isDetailPage={true}
      hasSubpage={data?.entityPageModule?.properties?.hasSubpage}
    >
      <Styled.Wrapper>
        <NewsList />
      </Styled.Wrapper>
    </NewsContextProvider>
  );
};

const Fallback: React.FC = () => {
  return (
    <Row>
      <Column sizeDesktop={10} offsetDesktop={1} size={12}>
        <NewsListSkeleton loadingSkeletonCount={3} showActors={false} />
      </Column>
    </Row>
  );
};

const ErrorFallback: React.FC<{
  onReload?: () => void;
}> = ({ onReload = () => window.location.reload() }) => {
  return <ModuleError refetch={onReload} />;
};

export const NewsSubpage: React.FC = () => {
  return (
    <ErrorBoundary onError={() => <ErrorFallback />}>
      <Suspense fallback={<Fallback />}>
        <NewsSubpageInner />
      </Suspense>
    </ErrorBoundary>
  );
};
